import { ellipsisAccesibility, renameCalendarAccessibility } from '../strings.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import CalendarNodeTextField from './CalendarNodeTextField';
import CalendarPickerCalendarContextMenu from './CalendarPickerCalendarContextMenu';
import getCalendarLoadingState from '../selectors/getCalendarLoadingState';
import getContextMenuViewStateForItem from '../selectors/getContextMenuViewStateForItem';
import isRenamingItem from '../selectors/isRenamingItem';
import { isSelectedCalendar } from 'owa-calendar-surface-calendars-store';
import { renameCalendar } from 'owa-calendar-update';
import { assertNever } from 'owa-assert';
import {
    confirmAndDeleteCalendar,
    showCalendarContextMenu,
    userSelectedCalendar,
} from '../actions/calendarActions';
import { ControlIcons } from 'owa-control-icons';
import getAnchorElementForContextMenu from '../utils/getAnchorElementForContextMenu';
import {
    getCalendarColorScheme,
    getDefaultColorSchemesOrderedByColorIndex,
    isCalendarColorResolved,
} from 'owa-calendar-color-resolver';
import { getCalendarEntry, getDefaultCalendar, Calendar } from 'owa-calendar-cache';
import { hideContextMenu } from '../mutators/contextMenuMutators';
import { Icon } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { startedRenaming, stoppedRenaming } from '../mutators/renamingMutators';
import { ColorScheme, colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import { DefaultButton, IButton, IconButton } from '@fluentui/react/lib/Button';
import { getPalette } from 'owa-theme';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { getFlexContainerStyle } from './getFlexContainerStyle';
import CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import { logUsage } from 'owa-analytics';
import { showSelectedCalendarOnly } from 'owa-calendar-surface-select-calendar';

const styles = require('./CalendarPicker.scss');
const classNames = require('classnames/bind').bind(styles);

/**
 * Calendar node properties
 * className: The class name for the root element.
 * calendar: The instance of calendar.
 * onDeleteCalendar: Callback for when the calendar is deleted.
 * showContextMenuButton: If the context menu should be shown
 */
export interface CalendarPickerCalendarProps {
    scenarioId: CalendarPickerScenarioId;
    selected: boolean;
    calendar: Calendar;
    onDeleteCalendar?: () => void;
    showContextMenuButton: boolean;
    hasMultipleAccounts: boolean;
    isCondensed: boolean;
    prevCalendarId?: string;
    nextCalendarId?: string;
}

interface CalendarPickerCalendarState {
    contextMenuHasKeyboardFocus: boolean;
    contextMenuIsActive: boolean;
}

@observer
export default class CalendarPickerCalendar extends React.Component<
    CalendarPickerCalendarProps,
    CalendarPickerCalendarState
> {
    private availableColors: ColorScheme[];
    private buttonRef: IButton;
    private contextMenuButtonRef: IButton;
    private focusTimerId: number | null;

    public constructor(props: CalendarPickerCalendarProps) {
        super(props);
        this.availableColors = getDefaultColorSchemesOrderedByColorIndex();
        this.state = { contextMenuHasKeyboardFocus: false, contextMenuIsActive: false };
    }

    render() {
        const {
            scenarioId,
            calendar,
            hasMultipleAccounts,
            isCondensed,
            prevCalendarId,
            nextCalendarId,
        } = this.props;
        const calendarEntry = getCalendarEntry(calendar);

        const foundSelectedCalendarResult = isSelectedCalendar(calendar);
        const defaultCalendar = getDefaultCalendar(calendar.id.mailboxInfo.userIdentity);
        const isDefaultCalendar = defaultCalendar && calendar.id === defaultCalendar.id;

        const palette = getPalette();
        const itemStyle: React.CSSProperties = foundSelectedCalendarResult
            ? colorSchemeToCss(getCalendarColorScheme(calendar.id, isDefaultCalendar))
            : {
                  borderColor: palette.neutralTertiary,
                  color: 'transparent',
              };

        const isRenaming = isRenamingItem(scenarioId, calendar.id.id);
        const contextMenu = getContextMenuViewStateForItem(scenarioId, calendar.id.id);

        const isNested = hasMultipleAccounts && !isCondensed;

        const addCalendarModalVisible = scenarioId === CalendarPickerScenarioId.AddCalendarModal;

        return isRenaming ? (
            <div
                className={classNames(styles.calendarListItem, {
                    selected: this.props.selected,
                    indentRight: isNested,
                    renameExpanded: isNested,
                })}>
                {this.renderCalendarState(itemStyle, foundSelectedCalendarResult)}
                <CalendarNodeTextField
                    className={classNames(styles.textboxCalendarName, {
                        smallIconMargin: isNested,
                        shortenField: !isNested,
                    })}
                    onEntry={this.onNewCalendarName}
                    onDismiss={this.onCalendarNameDismiss}
                    defaultValue={calendar.displayName}
                    ariaLabel={loc(renameCalendarAccessibility)}
                />
            </div>
        ) : (
            <div
                className={classNames(styles.calendarWrapper, {
                    addCalendarModalIndentRight: addCalendarModalVisible && isNested,
                })}>
                <div
                    className={classNames(styles.listItemContainer, {
                        contextMenuEnabled: this.props.showContextMenuButton,
                        contextMenuFocused: this.state.contextMenuHasKeyboardFocus,
                        contextMenuActive: this.state.contextMenuIsActive,
                    })}>
                    <DefaultButton
                        aria-selected={!!foundSelectedCalendarResult}
                        role={'option'}
                        className={classNames(styles.calendarListCalendarButton, {
                            selected: this.props.selected,
                            addCalendarListCalendarButton: addCalendarModalVisible,
                            indentRight: !addCalendarModalVisible && isNested,
                            indentedAddCalendarListCalendarButton:
                                addCalendarModalVisible && isNested,
                        })}
                        onClick={this.onCalendarClicked}
                        onContextMenu={!addCalendarModalVisible ? this.onContextMenu : undefined}
                        onKeyDown={this.onOnKeyDown}
                        title={calendar.displayName}
                        styles={getFlexContainerStyle()}
                        componentRef={this.setButtonRef}>
                        {addCalendarModalVisible
                            ? this.renderCalendarColorIcon()
                            : this.renderCalendarState(itemStyle, foundSelectedCalendarResult)}

                        <div
                            className={classNames(styles.listItemLabel, {
                                addCalendarListItemLabel: addCalendarModalVisible,
                                smallIconMargin: isNested,
                            })}>
                            {calendar.displayName}
                        </div>

                        <CalendarPickerCalendarContextMenu
                            calendar={calendar}
                            calendarEntry={calendarEntry}
                            contextMenuViewState={contextMenu}
                            availableColors={this.availableColors}
                            prevCalendarId={prevCalendarId}
                            nextCalendarId={nextCalendarId}
                            onDismiss={this.onContextMenuDismiss}
                            onRename={this.onContextMenuRename}
                            onRemove={this.onContextMenuRemove}
                            onNewColorChosen={this.onNewColorChosen}
                            onShowThisOnly={this.onContextMenuShowThisOnly}
                        />
                    </DefaultButton>
                    {this.props.showContextMenuButton && (
                        <IconButton
                            data-is-focusable={false}
                            ariaLabel={loc(ellipsisAccesibility)}
                            title={loc(ellipsisAccesibility)}
                            className={styles.listItemContextMenuButton}
                            iconProps={{ iconName: ControlIcons.More }}
                            onClick={this.onContextMenuButton}
                            componentRef={this.setContextMenuButtonRef}
                            onKeyDown={this.contextMenuOnKeyDown}
                            onFocus={this.contextMenuButtonOnFocus}
                            onBlur={this.contextMenuButtonOnBlur}
                        />
                    )}
                </div>
            </div>
        );
    }

    focus() {
        if (this.buttonRef && !this.focusTimerId) {
            // using setTimeout to avoid maximum callstack exception whentrying to focus the calendar picker while callout is dismissing
            this.focusTimerId = window.setTimeout(() => {
                // As the button ref might not be available when this is triggered so we check again
                if (this.buttonRef) {
                    this.buttonRef.focus();
                }
                this.focusTimerId = null;
            }, 0);
        }
    }

    componentWillUnmount() {
        window.clearTimeout(this.focusTimerId);
    }

    private renderCalendarState(
        itemStyle: React.CSSProperties,
        foundSelectedCalendarResult: boolean
    ): JSX.Element {
        const loadingState = getCalendarLoadingState(this.props.calendar);
        switch (loadingState) {
            case 'Loading':
                return (
                    <Spinner className={styles.calendarLoadingSpinner} size={SpinnerSize.small} />
                );
            case 'CalendarProviderUnauthorized':
            case 'Throttled':
            case 'Failed':
                return (
                    <span style={itemStyle} className={styles.checkMarkCircle}>
                        !
                    </span>
                );
            case 'Loaded':
            case undefined:
                if (foundSelectedCalendarResult) {
                    itemStyle.border = 'none';
                }

                return (
                    <Icon
                        style={itemStyle}
                        className={styles.checkMarkCircle}
                        iconName={foundSelectedCalendarResult && ControlIcons.StatusCircleCheckmark}
                    />
                );
            default:
                return assertNever(loadingState);
        }
    }

    private renderCalendarColorIcon(): JSX.Element {
        const palette = getPalette();
        const itemStyle: React.CSSProperties = isCalendarColorResolved(this.props.calendar.id)
            ? { color: getCalendarColorScheme(this.props.calendar.id).calPrimary }
            : { color: palette.neutralPrimary };
        return (
            <Icon
                style={itemStyle}
                className={styles.colorIconCirlce}
                iconName={
                    isCalendarColorResolved(this.props.calendar.id)
                        ? ControlIcons.CircleFill
                        : ControlIcons.CircleRing
                }
            />
        );
    }

    private onNewColorChosen = () => {
        logUsage(
            'CalendarUpdated',
            {
                source_1: 'CalendarPicker',
            },
            {
                isCore: true,
            }
        );
        this.forceUpdate();
    };

    private onCalendarClicked = () => {
        const { calendar } = this.props;
        userSelectedCalendar(calendar);
    };

    private onContextMenuDismiss = () => {
        hideContextMenu(this.props.scenarioId);
        this.setState({ contextMenuIsActive: false });
        this.focus();
    };

    private onContextMenuRename = () => {
        startedRenaming(this.props.scenarioId, this.props.calendar.id.id);
    };

    private onContextMenuRemove = () => {
        confirmAndDeleteCalendar(this.props.calendar, () => {
            if (this.props.onDeleteCalendar) {
                this.props.onDeleteCalendar();
            }
        });
    };

    private onContextMenuShowThisOnly = () => {
        showSelectedCalendarOnly(this.props.calendar);
    };

    /**
     * In the case of the onHover context menu entry point button we don't want clicking it
     * to invoke the parent button as well.
     */
    private onContextMenuButton = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.stopPropagation();

        this.onContextMenu(ev);
    };

    private onContextMenu = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault();

        this.setState({ contextMenuIsActive: true });
        showCalendarContextMenu(
            this.props.scenarioId,
            this.props.calendar.id.id,
            getAnchorElementForContextMenu(ev)
        );
    };

    private onOnKeyDown = (ev: React.KeyboardEvent<unknown>) => {
        if (this.props.showContextMenuButton) {
            switch (ev.which) {
                case KeyCodes.right:
                    ev.stopPropagation();
                    this.contextMenuButtonRef.focus();
                    break;
            }
        }
    };

    private contextMenuOnKeyDown = (ev: React.KeyboardEvent<unknown>) => {
        if (this.props.showContextMenuButton) {
            switch (ev.which) {
                case KeyCodes.left:
                    ev.stopPropagation();
                    this.buttonRef.focus();
                    break;
            }
        }
    };

    private onNewCalendarName = async (newCalendarName: string) => {
        logUsage(
            'CalendarUpdated',
            {
                source_1: 'CalendarPicker',
            },
            {
                isCore: true,
            }
        );
        stoppedRenaming(this.props.scenarioId);
        await renameCalendar(this.props.calendar, newCalendarName);
        this.focus();
    };

    private onCalendarNameDismiss = () => {
        stoppedRenaming(this.props.scenarioId);
        this.focus();
    };

    private setButtonRef = (button: IButton) => {
        this.buttonRef = button;
    };

    private setContextMenuButtonRef = (button: IButton) => {
        this.contextMenuButtonRef = button;
    };

    private contextMenuButtonOnFocus = () => {
        this.setState({ contextMenuHasKeyboardFocus: true });
    };

    private contextMenuButtonOnBlur = () => {
        this.setState({ contextMenuHasKeyboardFocus: false });
    };
}
